import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, CAT_ICON, REPEATS, fmtDay, origin, vibrate, waUrl } from "../lib/api";
import { useApp } from "../lib/app";
import { AiChip, AiLoader, Avatar, PersonChip, Sheet } from "./bits";
import { useT } from "../lib/i18n";

export function Messages({ messages, members, onDone }) {
  const byId = Object.fromEntries((members || []).map((m) => [m.id, m]));
  const t = useT();
  return (
    <div data-testid="wa-messages" className="space-y-3">
      <p className="ul-label">{t("Send messages on WhatsApp")}</p>
      {messages.map((m) => (
        <div key={m.member_id} className="ul-card p-4">
          <div className="flex items-center gap-2 mb-2"><Avatar m={byId[m.member_id]} size={28} /><b>{m.name}</b>{m.ai !== false && <AiChip text="Written by AI" />}</div>
          <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-[#2A2540] mb-3" data-testid={`wa-text-${m.name.toLowerCase()}`}>{m.text}</p>
          <a data-testid={`wa-send-${m.name.toLowerCase()}`} href={waUrl(m.text)} target="_blank" rel="noreferrer" className="ul-btn ul-wa w-full">{t("Send to {name} on WhatsApp", { name: m.name })}</a>
        </div>
      ))}
      {onDone && <button data-testid="messages-done-btn" className="ul-btn ul-primary w-full" onClick={onDone}>{t("Done")}</button>}
    </div>
  );
}

function PersonPicker({ open, onClose, onPick, members }) {
  const t = useT();
  return (
    <Sheet open={open} onClose={onClose} testid="person-picker">
      <h3 className="font-display text-xl font-bold mb-3">{t("Who should do this?")}</h3>
      <div className="grid gap-2">
        {members.filter((m) => m.type !== "child" && m.status !== "pending").map((m) => (
          <button key={m.id} data-testid={`pick-${m.name.toLowerCase()}`} className="ul-card p-3 flex items-center gap-3 text-left" onClick={() => { onPick(m.id); onClose(); }}>
            <Avatar m={m} /><span><b>{m.name}</b><span className="block text-xs text-[#6B6680]">{m.note || m.relation}</span></span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

function TaskEdit({ t, i, set, members, onPick, onDelete }) {
  const tr = useT();
  const m = members.find((x) => x.id === t.assignee_id);
  return (
    <motion.div layout exit={{ x: 160, y: -260, scale: 0.2, opacity: 0, transition: { duration: 0.6, delay: i * 0.08 } }}
      data-testid={`review-task-${i}`} className="ul-card p-4 space-y-2">
      <div className="flex items-start gap-2">
        <span className="text-2xl">{CAT_ICON[t.category] || "🏠"}</span>
        <div className="flex-1 min-w-0">
          <input data-testid={`review-title-${i}`} className="w-full font-bold bg-transparent outline-none border-b border-transparent focus:border-[#E4DEFA]" value={t.title} onChange={(e) => set({ title: e.target.value })} />
          {t.original_words && <p className="text-xs text-[#6B6680]">({t.original_words})</p>}
        </div>
        <PersonChip m={m} onClick={onPick} testid={`review-person-${i}`} />
        <button data-testid={`review-delete-${i}`} onClick={onDelete} className="text-[#9C97AE] p-1" aria-label="delete"><Trash2 size={18} /></button>
      </div>
      <div className="flex flex-wrap gap-2 text-sm">
        <input type="date" data-testid={`review-date-${i}`} className="ul-input !min-h-[40px] !w-auto !py-1" value={t.due_date || ""} onChange={(e) => set({ due_date: e.target.value })} />
        <select data-testid={`review-repeat-${i}`} className="ul-input !min-h-[40px] !w-auto !py-1" value={t.repeat || "none"} onChange={(e) => set({ repeat: e.target.value })}>
          {REPEATS.map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}
        </select>
        {t.repeat === "every_n" && <input type="number" className="ul-input !min-h-[40px] !w-20 !py-1" value={t.repeat_days || 30} onChange={(e) => set({ repeat_days: Number(e.target.value) })} />}
      </div>
      {t.reason && <p className="text-sm text-[#6B6680]" data-testid={`review-reason-${i}`}>{t.reason}</p>}
      {t.category === "medicine" && <p className="text-xs text-[#8A6000]">{tr("Reminder only · follow your doctor's advice.")}</p>}
      {t.checklist?.length > 0 && <ul className="text-sm list-disc pl-5 text-[#6B6680]">{t.checklist.map((c, k) => <li key={k}>{typeof c === "string" ? c : c.text}</li>)}</ul>}
    </motion.div>
  );
}

export default function Review({ result, onSaved, onEdit }) {
  const { me } = useApp();
  const tr = useT();
  const members = me?.members || [];
  const [tasks, setTasks] = useState(result.tasks || []);
  const [leaves, setLeaves] = useState(result.leaves || []);
  const [grocery, setGrocery] = useState(result.grocery || []);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [gone, setGone] = useState(false);

  const n = tasks.length + leaves.length + (grocery.length ? 1 : 0);
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/confirm", { tasks, leaves, grocery, origin: origin() });
      vibrate(); setGone(true);
      setTimeout(() => {
        toast.success(data.handed_off ? tr("{n} things off your mind 💜", { n: data.handed_off }) : tr("Saved 💜"));
        setSent(data);
      }, 700 + tasks.length * 80);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (sent) {
    return sent.messages?.length ? <Messages messages={sent.messages} members={members} onDone={() => onSaved?.(sent)} /> : (
      <div className="ul-card p-6 text-center" data-testid="review-saved">
        <div className="text-5xl mb-2">🌿</div><p className="font-display text-xl font-bold mb-4">{tr("All saved. One less thing to hold.")}</p>
        <button className="ul-btn ul-primary w-full" data-testid="review-saved-ok" onClick={() => onSaved?.(sent)}>{tr("Back home")}</button>
      </div>
    );
  }
  if (busy && !gone) return <AiLoader kind="send" />;

  return (
    <div className="space-y-3" data-testid="review-screen">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-xl font-extrabold" data-testid="review-header">{tr("✨ {n} tasks found · fair split suggested", { n: tasks.length })}</h2>
      </div>
      <AiChip />
      <div className="flex justify-end gap-1 pr-2">{members.filter((m) => m.type !== "child").map((m) => <Avatar key={m.id} m={m} size={26} />)}</div>
      <AnimatePresence>
        {!gone && tasks.map((t, i) => (
          <TaskEdit key={i} t={t} i={i} members={members} set={(p) => setTasks((ts) => ts.map((x, k) => (k === i ? { ...x, ...p } : x)))}
            onPick={() => setPick(i)} onDelete={() => setTasks((ts) => ts.filter((_, k) => k !== i))} />
        ))}
      </AnimatePresence>
      {leaves.map((l, i) => (
        <div key={i} className="ul-card p-4 flex items-center gap-3" data-testid={`review-leave-${i}`}>
          <span className="text-2xl">🧹</span>
          <p className="flex-1"><b>{tr("Log leave: {h}", { h: l.helper })}</b><span className="block text-sm text-[#6B6680]">{l.dates.map(fmtDay).join(", ")}</span></p>
          <button onClick={() => setLeaves((ls) => ls.filter((_, k) => k !== i))} className="text-[#9C97AE]"><Trash2 size={18} /></button>
        </div>
      ))}
      {grocery.length > 0 && (
        <div className="ul-card p-4" data-testid="review-grocery">
          <p className="ul-label mb-2">{tr("🛒 Add to grocery list ({n})", { n: grocery.length })}</p>
          {grocery.map((g, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F3F0FF] last:border-0">
              <span>{g.name}{g.original ? ` (${g.original})` : ""}{g.qty ? ` – ${g.qty}` : ""}</span>
              <button onClick={() => setGrocery((gs) => gs.filter((_, k) => k !== i))} className="text-[#9C97AE]"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
      {(result.autopay || []).map((a, i) => (
        <div key={i} className="rounded-2xl p-4 text-sm" style={{ background: "#F3F0FF" }} data-testid={`autopay-note-${i}`}>
          💡 {tr("{a} can go on autopay. Set it up once in your bank or UPI app, and that's one less thing to remember.", { a })}
        </div>
      ))}
      {n === 0 && !gone && <p className="text-center text-[#6B6680] py-4">{tr("No tasks left. Add some by editing, or go back.")}</p>}
      <p className="text-center text-sm text-[#6B6680]">{tr("Nothing is saved until you confirm.")}</p>
      <div className="flex gap-2">
        <button data-testid="review-confirm-btn" disabled={busy || n === 0} className="ul-btn ul-primary flex-1" onClick={confirm}>{tr("Confirm and share")}</button>
        {onEdit && <button data-testid="review-edit-btn" className="ul-btn ul-ghost" onClick={onEdit}>{tr("Edit")}</button>}
      </div>
      <PersonPicker open={pick !== null} onClose={() => setPick(null)} members={members}
        onPick={(id) => setTasks((ts) => ts.map((x, k) => (k === pick ? { ...x, assignee_id: id, reason: x.assignee_id === id ? x.reason : "" } : x)))} />
    </div>
  );
}

export { PersonPicker };
